import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FiArrowUpRight, FiChevronRight } from 'react-icons/fi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import './ModernMegaMenu.css';

const ModernMegaMenu = ({ title, data }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Graceful fallback if no data provided
  if (!data || !data.columns) return null;

  // Check if any of the child items match the current path
  const isParentActive = data.columns.some((col) =>
    col.items.some((item) => currentPath === item.link)
  );
  const highlight = data.highlight || {};
  const mediaType = highlight.mediaType || 'none';
  const normalizedSlides = (highlight.images || [])
    .map((item, idx) => {
      const isArrayItem = Array.isArray(item);
      const isObjectItem = item && typeof item === 'object' && !isArrayItem;
      const imgSrc = isObjectItem ? item.image : (isArrayItem ? item[0] : item);
      const name = isObjectItem
        ? item.name
        : (isArrayItem ? item[1] : (highlight.names ? highlight.names[idx] : null));
      const url = isObjectItem ? item.url : (isArrayItem ? item[2] : null);
      const key = (isObjectItem && (item.id || item.url || item.image)) || (isArrayItem && (item[2] || item[0])) || `${idx}`;

      return { key, imgSrc, name, url, idx };
    })
    .filter((slide) => Boolean(slide.imgSrc));

  return (
    <li className="nav-item dropdown modern-mega-parent tp-static">
      {/* Menu Toggle Label */}
      <a className={`nav-link modern-mega-toggle ${isParentActive ? 'active' : ''}`} href="/" onClick={(e) => e.preventDefault()}>
        {title}
        <FiChevronRight className="modern-mega-toggle-arrow" />
      </a>

      {/* Dropdown Container */}
      <div className="modern-mega-menu">
        <div className="modern-mega-wrapper">

          {/* Left Side: Dynamic Columns */}
          <div className="modern-mega-left">
            {data.columns.map((col, colIndex) => (
              <div key={colIndex} className="modern-mega-col">
                {/* Column Title (e.g. "Services", "Company") */}
                <h4 className="modern-mega-col-title">{col.title}</h4>

                {/* Column Items */}
                <div className="modern-mega-items">
                  {col.items.map((item, itemIndex) => {
                    const isActive = currentPath === item.link;

                    // Render as Link for internal routes, <a> for external links
                    const isExternal = item.target || item.link.startsWith('http');

                    const itemContent = (
                      <>
                        {/* Icon */}
                        <div className="modern-mega-item-icon">
                          {item.icon}
                        </div>

                        {/* Content */}
                        <div className="modern-mega-item-content">
                          <div className="modern-mega-item-header">
                            <span className="modern-mega-item-title">{item.title}</span>
                            {/* Optional Badge */}
                            {item.badge && (
                              <span className="modern-mega-item-badge">{item.badge}</span>
                            )}
                          </div>
                          {/* Subtitle/Description */}
                          {item.description && (
                            <p className="modern-mega-item-desc">{item.description}</p>
                          )}
                        </div>

                        {/* Right Arrow */}
                        <FiChevronRight className="modern-mega-item-arrow" />
                      </>
                    );

                    if (isExternal) {
                      return (
                        <a
                          key={itemIndex}
                          href={item.link}
                          target={item.target ? '_blank' : '_self'}
                          rel={item.target ? 'noreferrer' : ''}
                          className={`modern-mega-item-link ${isActive ? 'active' : ''}`}
                        >
                          {itemContent}
                        </a>
                      );
                    } else {
                      return (
                        <Link
                          key={itemIndex}
                          to={item.link}
                          className={`modern-mega-item-link ${isActive ? 'active' : ''}`}
                        >
                          {itemContent}
                        </Link>
                      );
                    }
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Right Side: Highlight / Media Section */}
          {highlight && (
            <div className="modern-mega-right">
              <div className="modern-mega-highlight-card">

                {/* Highlight Texts */}
                <h4 className="highlight-title">{highlight.title}</h4>
                <p className="highlight-desc">{highlight.subtitle}</p>

                {/* Highlight Media */}
                <div className="highlight-media">
                  {mediaType === 'video' && highlight.mediaSrc ? (
                    <>
                      {/* Video element */}
                      <video
                        src={highlight.mediaSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    </>
                  ) : mediaType === 'slider' && normalizedSlides.length > 0 ? (
                    <Swiper
                      modules={[Autoplay, EffectFade,]}
                      effect="fade"
                      loop={true}
                      autoplay={{ delay: 3000, disableOnInteraction: false }}
                      className="highlight-swiper"
                    >
                      {normalizedSlides.map((slide) => {
                        return (
                          <SwiperSlide key={slide.key}>
                            {slide.url ? (
                              <a href={slide.url} target="_blank" rel="noreferrer">
                                <img src={slide.imgSrc} alt={slide.name || `Highlight ${slide.idx}`} loading='lazy'/>
                                {slide.name && <span className='slide-number'>{slide.name}</span>}
                              </a>
                            ) : (
                              <>
                                <img src={slide.imgSrc} alt={slide.name || `Highlight ${slide.idx}`} loading='lazy'/>
                                {slide.name && <span className='slide-number'>{slide.name}</span>}
                              </>
                            )}
                          </SwiperSlide>
                        );
                      })}
                    </Swiper>
                  ) : mediaType === 'image' && highlight.mediaSrc ? (
                    <img src={highlight.mediaSrc} alt="Highlight Update" loading='lazy'/>
                  ) : null}
                </div>

                {/* Highlight Action Link */}
                {highlight.actionText && (
                  <a href={highlight.actionLink} className="highlight-action">
                    {highlight.actionText} <FiArrowUpRight style={{ fontSize: '18px' }} />
                  </a>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </li>
  );
};

export default ModernMegaMenu;
